import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Tabs, Tab, Avatar, Grid, Snackbar, Alert, Tooltip, CircularProgress
} from '@mui/material';
import {
  Search, Visibility, CheckCircle, Cancel, AccessTime,
  Close, Person, FamilyRestroom, Download, Refresh
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import apiClient from '../../services/apiClient';

const statusConfig = {
  pending:    { label: 'Pending Review', color: 'warning' },
  reviewing:  { label: 'Reviewing',      color: 'info' },
  approved:   { label: 'Approved',       color: 'success' },
  rejected:   { label: 'Rejected',       color: 'error' },
  waitlist:   { label: 'Waitlist',       color: 'default' },
};

const tabToStatus = ['all', 'pending', 'approved', 'rejected'];
const tabRoutes   = ['/admin/applications', '/admin/applications/pending', '/admin/applications/approved', '/admin/applications/rejected'];

const toDisplay = (app) => ({
  id:            app.application_id,
  firstName:     app.student_first_name,
  lastName:      app.student_last_name,
  dob:           app.date_of_birth ? new Date(app.date_of_birth).toLocaleDateString() : '—',
  gradeApplying: app.grade_applying_for,
  termYear:      app.term_year,
  submittedDate: app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : '—',
  status:        app.status || 'pending',
  parent:        [app.parent1_first_name, app.parent1_last_name].filter(Boolean).join(' ') || '—',
  phone:         app.phone || app.parent1_phone || '—',
  email:         app.parent1_email || '—',
  city:          app.city || '—',
  state:         app.state || '—',
  church:        app.church || '—',
  pastor:        app.pastor || '—',
  howHeard:      app.how_did_you_hear || '—',
  comments:      app.additional_comments || '',
  _raw:          app,
});

const ApplicationsAdmin = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const getInitialTab = () => {
    if (location.pathname.includes('/pending'))  return 1;
    if (location.pathname.includes('/approved')) return 2;
    if (location.pathname.includes('/rejected')) return 3;
    return 0;
  };

  const [tab, setTab]             = useState(getInitialTab());
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState(null);
  const [detail, setDetail]       = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [applications, setApplications]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [snackbar, setSnackbar]   = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', app: null });

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const statusFilter = tabToStatus[tab] !== 'all' ? tabToStatus[tab] : undefined;
      const data = await apiClient.getApplications({
        status: statusFilter,
        search: search || undefined,
        limit: 200
      });
      setApplications((data.applications || []).map(toDisplay));
      setTotal(data.total || 0);
    } catch (err) {
      setSnackbar({ open: true, message: `Failed to load applications: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [tab, search]);

  useEffect(() => {
    const timer = setTimeout(fetchApplications, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [fetchApplications, search]);

  const openDetail = async (app) => {
    setSelected(app);
    setDetailLoading(true);
    try {
      const full = await apiClient.getApplicationById(app.id);
      setDetail(toDisplay(full));
    } catch {
      setDetail(app);
    } finally {
      setDetailLoading(false);
    }
  };

  const counts = {
    all:      total,
    pending:  applications.filter(a => a.status === 'pending' || a.status === 'reviewing').length,
    approved: applications.filter(a => a.status === 'approved').length,
    rejected: applications.filter(a => a.status === 'rejected').length,
  };

  const handleStatusChange = (app, newStatus) => {
    setConfirmDialog({ open: true, action: newStatus, app });
  };

  const confirmStatusChange = async () => {
    const { app, action } = confirmDialog;
    setConfirmDialog({ open: false, action: '', app: null });
    try {
      await apiClient.updateApplicationStatus(app.id, action);
      setApplications(prev => prev.map(a => a.id === app.id ? { ...a, status: action } : a));
      if (detail?.id === app.id) setDetail(d => ({ ...d, status: action }));
      const labels = { approved: 'Approved', rejected: 'Rejected', pending: 'Moved to Pending', reviewing: 'Marked as Reviewing' };
      setSnackbar({ open: true, message: `${app.firstName} ${app.lastName}: ${labels[action]}`, severity: action === 'approved' ? 'success' : action === 'rejected' ? 'error' : 'info' });
    } catch (err) {
      setSnackbar({ open: true, message: `Failed to update: ${err.message}`, severity: 'error' });
    }
  };

  const displayedApp = detail || selected;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Application Management</Typography>
          <Typography variant="body2" color="text.secondary">{total} total applications</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchApplications} disabled={loading}>Refresh</Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[['all','primary'],['pending','warning'],['approved','success'],['rejected','error']].map(([key, color]) => (
          <Grid item xs={6} sm key={key}>
            <Paper elevation={1} sx={{ p: 2, textAlign: 'center', borderRadius: 2, cursor: 'pointer',
              border: tabToStatus[tab] === key ? '2px solid' : '2px solid transparent',
              borderColor: tabToStatus[tab] === key ? `${color}.main` : 'transparent' }}
              onClick={() => { const i = tabToStatus.indexOf(key); setTab(i); navigate(tabRoutes[i] || tabRoutes[0]); }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: `${color}.main` }}>
                {loading ? '—' : counts[key]}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{key}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(e, v) => { setTab(v); navigate(tabRoutes[v] || tabRoutes[0]); }}>
            <Tab label={`All (${loading ? '…' : counts.all})`} />
            <Tab label={`Pending (${loading ? '…' : counts.pending})`} />
            <Tab label={`Approved (${loading ? '…' : counts.approved})`} />
            <Tab label={`Rejected (${loading ? '…' : counts.rejected})`} />
          </Tabs>
        </Box>

        <Box sx={{ p: 2 }}>
          <TextField
            placeholder="Search by name or parent..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            size="small" sx={{ minWidth: 320 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>Applicant</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Grade</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Parent / Guardian</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Submitted</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
              )}
              {!loading && applications.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No applications found</TableCell></TableRow>
              )}
              {!loading && applications.map(app => (
                <TableRow key={app.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.light', fontSize: '0.9rem' }}>
                        {app.firstName?.charAt(0)}{app.lastName?.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{app.firstName} {app.lastName}</Typography>
                        <Typography variant="caption" color="text.secondary">{app.id?.slice(0,8)}…</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{app.gradeApplying}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{app.parent}</Typography>
                    <Typography variant="caption" color="text.secondary">{app.phone}</Typography>
                  </TableCell>
                  <TableCell>{app.submittedDate}</TableCell>
                  <TableCell>
                    <Chip
                      label={(statusConfig[app.status] || statusConfig.pending).label}
                      color={(statusConfig[app.status] || statusConfig.pending).color}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => openDetail(app)}><Visibility fontSize="small" /></IconButton>
                      </Tooltip>
                      {app.status !== 'approved' && (
                        <Tooltip title="Approve">
                          <IconButton size="small" color="success" onClick={() => handleStatusChange(app, 'approved')}><CheckCircle fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                      {app.status !== 'rejected' && (
                        <Tooltip title="Reject">
                          <IconButton size="small" color="error" onClick={() => handleStatusChange(app, 'rejected')}><Cancel fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                      {app.status !== 'reviewing' && (
                        <Tooltip title="Mark Reviewing">
                          <IconButton size="small" color="info" onClick={() => handleStatusChange(app, 'reviewing')}><AccessTime fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onClose={() => { setSelected(null); setDetail(null); }} maxWidth="md" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {displayedApp?.firstName} {displayedApp?.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Submitted {displayedApp?.submittedDate}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {displayedApp?.status && (
                  <Chip
                    label={(statusConfig[displayedApp.status] || statusConfig.pending).label}
                    color={(statusConfig[displayedApp.status] || statusConfig.pending).color}
                  />
                )}
                <IconButton onClick={() => { setSelected(null); setDetail(null); }} size="small"><Close /></IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {detailLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Person fontSize="small" color="primary" />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Student</Typography>
                    </Box>
                    {[
                      ['Full Name', `${displayedApp?.firstName} ${displayedApp?.lastName}`],
                      ['Date of Birth', displayedApp?.dob],
                      ['Grade Applying', displayedApp?.gradeApplying],
                      ['Term Year', displayedApp?.termYear],
                      ['City / State', `${displayedApp?.city}, ${displayedApp?.state}`],
                    ].map(([label, val]) => (
                      <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{val}</Typography>
                      </Box>
                    ))}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <FamilyRestroom fontSize="small" color="primary" />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Parent / Guardian</Typography>
                    </Box>
                    {[
                      ['Name', displayedApp?.parent],
                      ['Phone', displayedApp?.phone],
                      ['Email', displayedApp?.email],
                      ['Church', displayedApp?.church],
                      ['Pastor', displayedApp?.pastor],
                    ].map(([label, val]) => (
                      <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{val}</Typography>
                      </Box>
                    ))}
                  </Grid>
                  {displayedApp?.comments && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Additional Comments</Typography>
                      <Typography variant="body2">{displayedApp.comments}</Typography>
                    </Grid>
                  )}
                </Grid>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button onClick={() => { setSelected(null); setDetail(null); }}>Close</Button>
              {displayedApp?.status !== 'approved' && (
                <Button variant="contained" color="success" startIcon={<CheckCircle />}
                  onClick={() => { handleStatusChange(selected, 'approved'); setSelected(null); setDetail(null); }}>
                  Approve
                </Button>
              )}
              {displayedApp?.status !== 'reviewing' && (
                <Button variant="outlined" color="info" startIcon={<AccessTime />}
                  onClick={() => { handleStatusChange(selected, 'reviewing'); setSelected(null); setDetail(null); }}>
                  Review
                </Button>
              )}
              {displayedApp?.status !== 'rejected' && (
                <Button variant="outlined" color="error" startIcon={<Cancel />}
                  onClick={() => { handleStatusChange(selected, 'rejected'); setSelected(null); setDetail(null); }}>
                  Reject
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: '', app: null })} maxWidth="xs">
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>
            Mark <strong>{confirmDialog.app?.firstName} {confirmDialog.app?.lastName}</strong> as <strong>{confirmDialog.action}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: '', app: null })}>Cancel</Button>
          <Button variant="contained"
            color={confirmDialog.action === 'approved' ? 'success' : confirmDialog.action === 'rejected' ? 'error' : 'primary'}
            onClick={confirmStatusChange}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ApplicationsAdmin;
